import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  CButton,
  CTooltip,
  CSpinner,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CFormInput,
  CInputGroup,
  CFormSwitch,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  cilClipboard,
  cilBan,
  cilReload,
  cilPlus,
  cilShareAlt,
} from "@coreui/icons";
import { apiClient } from "services/Api";
import SearchSelect from "components/forms/SearchSelect";

const ShareButton = ({
  clientId,
  canEditPrivacy = false,
  canShareFinancials = false,
  privacyStatus,
  privacyDefaults,
  privacyOptions,
  onSubmitPrivacy,
  updatingPrivacy = false,
}) => {
  const [accessSalary, setAccessSalary] = useState(null);
  const [accessLimited, setAccessLimited] = useState(null);
  const [loadingType, setLoadingType] = useState(null); // "salary" | "limited"
  const [copyingType, setCopyingType] = useState(null);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [visible, setVisible] = useState(false);
  const normalizedPrivacyDefaults = useMemo(() => {
    if (!privacyDefaults) {
      return { allowAll: true, allowedUserIds: [] };
    }
    const allowAll =
      typeof privacyDefaults.allowAll === "boolean"
        ? privacyDefaults.allowAll
        : !!privacyDefaults.allow_salary_access_for_all_users;
    const allowedUserIds = Array.isArray(privacyDefaults.allowedUserIds)
      ? privacyDefaults.allowedUserIds
      : [];
    return { allowAll, allowedUserIds };
  }, [privacyDefaults]);
  const defaultsKey = useMemo(() => {
    const ids = [...normalizedPrivacyDefaults.allowedUserIds]
      .sort((a, b) => a - b)
      .join(",");
    return `${normalizedPrivacyDefaults.allowAll ? "1" : "0"}|${ids}`;
  }, [normalizedPrivacyDefaults]);
  const [privacyForm, setPrivacyForm] = useState(normalizedPrivacyDefaults);

  const baseUrl = `/projects/management/${clientId}/portal-access/`;

  /** GET – obter token ativo */
  const fetchAccess = useCallback(() => {
    setLoadingType("fetch");
    setError(null);
    setMessage(null);

    apiClient
      .get(baseUrl)
      .then((response) => {
        const data = response.data || {};
        if (
          Object.prototype.hasOwnProperty.call(data, "with_salary") ||
          Object.prototype.hasOwnProperty.call(data, "without_salary")
        ) {
          setAccessSalary(data.with_salary || null);
          setAccessLimited(data.without_salary || null);
        } else {
          // compatibilidade: se apenas um objeto, assumir como limitado
          setAccessSalary(null);
          setAccessLimited(data || null);
        }
      })
      .catch((err) => {
        if (err.response?.status === 404) {
          setAccessSalary(null);
          setAccessLimited(null);
        } else {
          setError("Erro ao obter o link público.");
        }
      })
      .finally(() => setLoadingType(null));
  }, [baseUrl]);

  useEffect(() => {
    setPrivacyForm(normalizedPrivacyDefaults);
  }, [defaultsKey, normalizedPrivacyDefaults]);

  useEffect(() => {
    fetchAccess();
  }, [fetchAccess]);

  const buildUrl = (token) =>
    token ? `${window.location.origin}/projetos/gestao/token/${token}` : "";

  const handleGenerate = (type) => {
    const allowSalary = type === "salary";
    setLoadingType(type);
    setError(null);
    setMessage(null);

    apiClient
      .post(baseUrl, {
        description: "Acesso portal cliente",
        allow_salary_access: allowSalary,
      })
      .then((response) => {
        const allowFromResponse = !!response.data?.allow_salary_access;
        if (allowFromResponse) {
          setAccessSalary(response.data);
        } else {
          setAccessLimited(response.data);
        }
        setMessage(
          allowFromResponse
            ? "Link público (acesso total) criado/renovado."
            : "Link público (acesso restrito) criado/renovado."
        );
      })
      .catch((err) => {
        if (err.response?.status === 403) {
          setError("Sem permissões para gerar link com dados financeiros.");
        } else {
          setError("Erro ao criar/renovar o link público.");
        }
      })
      .finally(() => setLoadingType(null));
  };

  const handleRevoke = (type) => {
    const allowSalary = type === "salary";
    const hasAccess = allowSalary ? accessSalary : accessLimited;
    if (!hasAccess) return;

    setLoadingType(type);
    setError(null);
    setMessage(null);

    apiClient
      .delete(baseUrl, {
        params: { allow_salary_access: allowSalary },
      })
      .then(() => {
        if (allowSalary) {
          setAccessSalary(null);
        } else {
          setAccessLimited(null);
        }
        setMessage(
          allowSalary
            ? "Link público (acesso total) revogado."
            : "Link público (acesso restrito) revogado."
        );
      })
      .catch(() => {
        setError("Erro ao revogar o link público.");
      })
      .finally(() => setLoadingType(null));
  };

  const handleCopy = async (type) => {
    const token =
      type === "salary" ? accessSalary?.token : accessLimited?.token;
    if (!token) return;

    const portalUrl = buildUrl(token);

    try {
      setCopyingType(type);
      await navigator.clipboard.writeText(portalUrl);
      setMessage("Link copiado para o clipboard.");
      setError(null);
    } catch {
      setError("Não foi possível copiar o link.");
      setMessage(null);
    } finally {
      setCopyingType(null);
    }
  };

  const handleVisibleChange = (nextVisible) => {
    setVisible(nextVisible);
    if (nextVisible) {
      setMessage(null);
      setError(null);
      fetchAccess();
    }
  };

  const handlePrivacyToggle = (checked) => {
    setPrivacyForm((prev) => ({ ...prev, allowAll: checked }));
  };

  const handlePrivacyUsersChange = (ids) => {
    setPrivacyForm((prev) => ({
      ...prev,
      allowedUserIds: Array.isArray(ids) ? ids : [],
    }));
  };

  const handlePrivacyReset = () => {
    setPrivacyForm(normalizedPrivacyDefaults);
  };

  const handlePrivacySave = () => {
    if (!onSubmitPrivacy) return;
    const result = onSubmitPrivacy({
      allowAll: privacyForm.allowAll,
      allowedUserIds: privacyForm.allowedUserIds,
    });
    if (result?.then) {
      result.then((success) => {
        if (success) {
          setVisible(false);
        }
      });
    }
  };

  return (
    <CDropdown
      alignment="end"
      visible={visible}
      onVisibleChange={handleVisibleChange}
      autoClose="outside"
    >
      {/* Botão na toolbar – sem seta, cantos arredondados */}
      <CDropdownToggle
        caret={false}
        color="info"
        className="d-flex align-items-center rounded-2"
        title="Gestão de partilha e privacidade"
      >
        <CIcon icon={cilShareAlt} className="me-2" size="lg" />
        Partilhar
        {privacyStatus && (
          <span
            className={`ms-2 rounded-circle bg-${privacyStatus.color} border border-white`}
            style={{ width: 15, height: 15 }}
            title={privacyStatus.description}
            aria-label={privacyStatus.label}
          ></span>
        )}
      </CDropdownToggle>

      <CDropdownMenu className="p-3 shadow-sm" style={{ minWidth: "520px" }}>
        <div className="mb-2">
          <strong>Partilha Externa</strong>
        </div>

        {[
          {
            title: "Link (acesso total)",
            description: "Inclui leitura e edição financeira dos RHs.",
            type: "salary",
            access: accessSalary,
            disabled: !canShareFinancials,
          },
          {
            title: "Link (acesso restrito)",
            description: "Sem acesso a dados salariais.",
            type: "limited",
            access: accessLimited,
            disabled: false,
          },
        ].map((item) => {
          const hasToken = !!item.access?.token;
          const url = buildUrl(item.access?.token);
          const isLoading =
            loadingType === item.type || loadingType === "fetch";
          const isCopying = copyingType === item.type;

          if (item.disabled) {
            return <></>;
          }

          return (
            <div key={item.type} className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <div>
                  <h6 className="mb-0">{item.title}</h6>
                  <div className="text-muted small">{item.description}</div>
                </div>
              </div>

              <CInputGroup size="sm">
                <CTooltip
                  content={
                    hasToken ? "Renovar link público" : "Criar link público"
                  }
                >
                  <CButton
                    color="primary"
                    variant="outline"
                    disabled={item.disabled || isLoading}
                    onClick={() => handleGenerate(item.type)}
                  >
                    {isLoading ? (
                      <CSpinner size="sm" />
                    ) : (
                      <CIcon icon={hasToken ? cilReload : cilPlus} />
                    )}
                  </CButton>
                </CTooltip>

                <CFormInput
                  type="text"
                  className="text-truncate"
                  value={url}
                  placeholder="Sem link público criado"
                  readOnly
                />

                <CTooltip content="Copiar link para o clipboard">
                  <CButton
                    color="secondary"
                    variant="outline"
                    disabled={!hasToken || isCopying || isLoading}
                    onClick={() => handleCopy(item.type)}
                  >
                    {isCopying ? (
                      <CSpinner size="sm" />
                    ) : (
                      <CIcon icon={cilClipboard} />
                    )}
                  </CButton>
                </CTooltip>

                <CTooltip content="Revogar link público">
                  <CButton
                    color="danger"
                    variant="outline"
                    disabled={!hasToken || isLoading}
                    onClick={() => handleRevoke(item.type)}
                  >
                    <CIcon icon={cilBan} />
                  </CButton>
                </CTooltip>
              </CInputGroup>
            </div>
          );
        })}

        {/* Info por baixo: título + instrução */}
        <div style={{ fontSize: "0.8rem" }}>
          <div className="text-muted">
            Partilhe este link com o cliente para dar acesso aos projetos.
          </div>
        </div>

        {(message || error) && (
          <div className="mt-2" style={{ fontSize: "0.75rem" }}>
            {message && <div className="text-success mb-1">{message}</div>}
            {error && <div className="text-danger">{error}</div>}
          </div>
        )}

        {canEditPrivacy && (
          <>
            <hr className="my-3" />
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>
                <strong>Privacidade Interna</strong>
              </div>
              {privacyStatus && (
                <span
                  className={`badge text-bg-${privacyStatus.color} text-white`}
                  title={privacyStatus.description}
                >
                  {privacyStatus.label}
                </span>
              )}
            </div>
            <CFormSwitch
              id="internalPrivacySwitch"
              color="primary"
              checked={privacyForm.allowAll}
              onChange={(event) => handlePrivacyToggle(event.target.checked)}
              disabled={updatingPrivacy}
              label={
                privacyForm.allowAll
                  ? "Toda a equipa interna pode consultar salários"
                  : "Acesso restrito"
              }
            />
            <div className="text-muted small mb-3">
              {privacyForm.allowAll
                ? "Utilize esta opção quando não pretende restringir salários."
                : "Selecione abaixo quais os utilizadores (além dos gestores) com acesso."}
            </div>
            <SearchSelect
              label="Utilizadores com acesso"
              options={privacyOptions || []}
              isMulti
              value={privacyForm.allowedUserIds}
              onChange={handlePrivacyUsersChange}
              disabled={privacyForm.allowAll || updatingPrivacy}
              placeholder="Escolha utilizadores internos"
              menuPortalTarget={
                typeof window !== "undefined" ? document.body : undefined
              }
            />
            <div className="d-flex justify-content-end gap-2 mt-3">
              <CButton
                color="secondary"
                variant="outline"
                onClick={handlePrivacyReset}
                disabled={updatingPrivacy}
              >
                Repor
              </CButton>
              <CButton
                color="primary"
                onClick={handlePrivacySave}
                disabled={updatingPrivacy}
              >
                {updatingPrivacy && (
                  <CSpinner component="span" size="sm" className="me-2" />
                )}
                Guardar
              </CButton>
            </div>
          </>
        )}
      </CDropdownMenu>
    </CDropdown>
  );
};

export default ShareButton;
